import { FC } from 'react';
import { InputNumber, Segmented, Switch } from 'antd';
import {
    TbAdjustments,
    TbBraces,
    TbChartBar,
    TbChartLine,
    TbChartPie,
    TbChecklist,
    TbCode,
    TbLayoutColumns,
    TbSettings,
    TbTable,
    TbVariable,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { SetStateMethod } from '@/types';

import type {
    VantageDisplayMode,
    VantageEditorPreferences,
    VantageQueryResult,
    VantageValidationResult,
    VantageVisualizationType,
} from '../../../../../types';
import { CollapsibleSection, SettingRow } from '../VantageSettingsPrimitives';

interface PreferencesTabProps {
    displayMode: VantageDisplayMode;
    limit: number;
    preferences: VantageEditorPreferences;
    result: VantageQueryResult | null;
    validation: VantageValidationResult | null;
    visualization: VantageVisualizationType;
    runQuery: () => void;
    setDisplayMode: SetStateMethod<VantageDisplayMode>;
    setLimit: SetStateMethod<number>;
    setPreferences: SetStateMethod<VantageEditorPreferences>;
    setVisualization: SetStateMethod<VantageVisualizationType>;
}

const PreferencesTab: FC<PreferencesTabProps> = ({
    displayMode,
    limit,
    preferences,
    result,
    runQuery,
    setDisplayMode,
    setLimit,
    setPreferences,
    setVisualization,
    validation,
    visualization,
}) => {
    const { token } = useAntDesignTheme();
    const optionIconStyle = { color: token.colorTextTertiary };
    const updatePreference = <Key extends keyof VantageEditorPreferences>(
        key: Key,
        value: VantageEditorPreferences[Key],
    ) => setPreferences((current) => ({ ...current, [key]: value }));

    return (
        <div>
            <CollapsibleSection
                title='Execution'
                description='Control validation, formatting, autocomplete, and query result limits.'
                icon={<TbSettings />}
            >
                <SettingRow
                    icon={<TbTable />}
                    label='Row Limit'
                    description='Set the maximum number of rows returned by an execution.'
                    control={
                        <InputNumber
                            min={1}
                            max={5000}
                            size='small'
                            value={limit}
                            onChange={(value) => setLimit(value ?? 500)}
                            style={{ width: 112 }}
                        />
                    }
                />
                <SettingRow
                    icon={<TbChecklist />}
                    label='Validate While Editing'
                    description='Continuously check the query against the governed schema.'
                    control={
                        <Switch
                            size='small'
                            checked={preferences.validateOnChange}
                            onChange={(checked) => updatePreference('validateOnChange', checked)}
                        />
                    }
                />
                <SettingRow
                    icon={<TbCode />}
                    label='Format SQL on Run or Save'
                    description='Normalize keywords and structurally indent SQL before it is persisted.'
                    control={
                        <Switch
                            size='small'
                            checked={preferences.formatSqlOnRun}
                            onChange={(checked) => updatePreference('formatSqlOnRun', checked)}
                        />
                    }
                />
                <SettingRow
                    last
                    icon={<TbVariable />}
                    label='Autocomplete'
                    description='Show governed tables, columns, and variables while typing.'
                    control={
                        <Switch
                            size='small'
                            checked={preferences.autocomplete}
                            onChange={(checked) => updatePreference('autocomplete', checked)}
                        />
                    }
                />
            </CollapsibleSection>
            <CollapsibleSection
                title='Output'
                description='Choose how query results are rendered inside the report component.'
                icon={<TbAdjustments />}
            >
                <SettingRow
                    icon={<TbChartBar />}
                    label='Visualization'
                    description='Select the primary visual treatment for returned rows.'
                    control={
                        <DropdownButtonSelect<VantageVisualizationType>
                            aria-label='Visualization'
                            matchTriggerWidth={false}
                            menuWidth='19rem'
                            size='small'
                            value={visualization}
                            style={{ width: 152 }}
                            onChange={(next) => {
                                if (!next) {
                                    return;
                                }

                                setVisualization(next);
                                if (!result && validation?.executable) {
                                    window.setTimeout(runQuery, 0);
                                }
                            }}
                            options={[
                                {
                                    value: 'table',
                                    label: 'Table',
                                    description: 'Review precise values in structured rows and columns.',
                                    icon: <TbTable style={optionIconStyle} />,
                                },
                                {
                                    value: 'bar',
                                    label: 'Bar Chart',
                                    description: 'Compare values across discrete categories.',
                                    icon: <TbChartBar style={optionIconStyle} />,
                                },
                                {
                                    value: 'line',
                                    label: 'Line Chart',
                                    description: 'Visualize trends and changes over time.',
                                    icon: <TbChartLine style={optionIconStyle} />,
                                },
                                {
                                    value: 'pie',
                                    label: 'Pie Chart',
                                    description: 'Show how categorical values contribute to a whole.',
                                    icon: <TbChartPie style={optionIconStyle} />,
                                },
                                {
                                    value: 'json',
                                    label: 'JSON',
                                    description: 'Inspect the returned records as structured JSON.',
                                    icon: <TbBraces style={optionIconStyle} />,
                                },
                            ]}
                        />
                    }
                />
                <SettingRow
                    icon={<TbCode />}
                    label='Display'
                    description='Show query code, its visualization, or both in the component.'
                    control={
                        <DropdownButtonSelect<VantageDisplayMode>
                            aria-label='Display'
                            matchTriggerWidth={false}
                            menuWidth='20rem'
                            size='small'
                            value={displayMode}
                            style={{ width: 184 }}
                            onChange={(next) => {
                                if (next) {
                                    setDisplayMode(next);
                                }
                            }}
                            options={[
                                {
                                    value: 'visualization',
                                    label: 'Visualization',
                                    description: 'Display only the selected result visualization.',
                                    icon: <TbChartBar style={optionIconStyle} />,
                                },
                                {
                                    value: 'codeAndVisualization',
                                    label: 'Code + Visualization',
                                    description: 'Display the SQL query alongside its rendered result.',
                                    icon: <TbLayoutColumns style={optionIconStyle} />,
                                },
                                {
                                    value: 'code',
                                    label: 'Code',
                                    description: 'Display only the SQL query used by the component.',
                                    icon: <TbCode style={optionIconStyle} />,
                                },
                            ]}
                        />
                    }
                />
                <SettingRow
                    last
                    icon={<TbTable />}
                    label='Result Density'
                    description='Adjust row spacing without changing the underlying result.'
                    control={
                        <Segmented
                            size='small'
                            value={preferences.resultDensity}
                            onChange={(value) => {
                                if (value === 'comfortable' || value === 'compact') {
                                    updatePreference('resultDensity', value);
                                }
                            }}
                            options={[
                                { value: 'comfortable', label: 'Comfortable' },
                                { value: 'compact', label: 'Compact' },
                            ]}
                        />
                    }
                />
            </CollapsibleSection>
        </div>
    );
};

export default PreferencesTab;
